// core/analytics.js — Google Analytics 4 + Consent Mode v2 (Fase B).
//
// Responsabilidades:
//   1. Injetar defaults do Google Consent Mode v2 com tudo "denied" ANTES do gtag.js.
//   2. Carregar gtag.js do Google Tag Manager (assíncrono).
//   3. Escutar `eloquence:consent-changed` e traduzir as finalidades LGPD
//      do modal para os sinais do Consent Mode v2.
//   4. Expor helpers para eventos customizados (trackEvent, trackLogin, etc.).
//
// Contrato público:
//   initAnalytics()            — chama uma vez no boot; idempotente.
//   trackEvent(name, params)   — dispara evento customizado.
//   applyConsent(snapshot)     — força atualização (usado por init e listener).
//
// Decisões travadas:
//   - ID de medição: G-27L06JGJSG (propriedade "App Eloquence" no GA4).
//   - Default Consent Mode v2: tudo "denied". Região BR (wait_for_update=500ms).
//   - Mapeamento finalidade → Consent Mode:
//       analise_uso   → analytics_storage
//       marketing     → ad_storage + ad_user_data + ad_personalization
//   - Se usuário não aceitar nenhuma: GA4 roda em modo cookieless (pings
//     agregados, sem ID de usuário). Consent Mode v2 oficial do Google.
//   - Usamos sessionStorage via loadConsentSnapshot() no boot para aplicar
//     consent imediatamente se o usuário já aceitou na sessão.
//   - Eventos emitidos pelo app:
//       'login'            — após auth.signIn bem-sucedido
//       'login_failed'     — erro de login
//       'consent_update'   — snapshot de consentimento novo/alterado
//       'lgpd_accept_all'  — usuário clicou em "Aceitar todos"
//       'lgpd_revoke'      — revogou uma finalidade na tela Meus consentimentos
//
// Notas de privacidade:
//   - Nunca enviar PII (email, nome completo, CPF) como param de evento.
//   - user_id só se houver consent de analytics (usar hash interno do Google).
//   - IP anonymization: GA4 faz por padrão, não precisa setar.

import { loadConsentSnapshot } from '../aluno/consent-modal.js';

const GA4_MEASUREMENT_ID = 'G-27L06JGJSG';
const SCRIPT_SRC = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;

let _inited = false;
let _loaded = false;

// ── dataLayer + gtag shim (precisa existir antes do script.js do Google) ─────

function _ensureDataLayer() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }
}

// ── API pública ──────────────────────────────────────────────────────────────

/**
 * Inicializa GA4 + Consent Mode v2.
 * Idempotente — múltiplas chamadas são no-op.
 *
 * Fluxo:
 *   1. Garante dataLayer + gtag.
 *   2. Registra Consent Mode default "denied" (tudo bloqueado).
 *   3. Carrega gtag.js assíncrono.
 *   4. Configura a propriedade GA4.
 *   5. Aplica consent do snapshot local se já existir.
 *   6. Registra listener pro evento 'eloquence:consent-changed'.
 */
export function initAnalytics() {
  if (_inited) return;
  _inited = true;

  try {
    _ensureDataLayer();

    // ① Defaults do Consent Mode v2 — TUDO denied até usuário decidir.
    // wait_for_update dá 500ms para a UI injetar update antes do primeiro ping.
    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'granted',  // preferências não rastreáveis
      security_storage: 'granted',       // autenticação/antifraude (sem opt-out)
      wait_for_update: 500,
      region: ['BR'],
    });

    // ② Config obrigatória do gtag (precisa vir antes do script externo).
    window.gtag('js', new Date());

    // ③ Carrega gtag.js assíncrono.
    _loadScript();

    // ④ Configura propriedade GA4.
    //    send_page_view default é true — a primeira página entra automaticamente.
    //    Não setamos 'client_storage' aqui; Consent Mode gerencia sozinho.
    window.gtag('config', GA4_MEASUREMENT_ID, {
      anonymize_ip: true,
      debug_mode: false,
    });

    // ⑤ Se já houver consent aceito na sessão atual (sessionStorage), aplica agora.
    const snapshot = loadConsentSnapshot();
    if (snapshot && snapshot.choices) {
      applyConsent(snapshot);
    }

    // ⑥ Listener para mudanças de consent (consent-modal ou lgpd-pages).
    window.addEventListener('eloquence:consent-changed', (ev) => {
      const snap = ev?.detail;
      if (snap && snap.choices) applyConsent(snap);
    });
  } catch (e) {
    console.warn('[analytics] init falhou; seguindo sem GA4:', e);
  }
}

/**
 * Traduz um snapshot de consentimento LGPD para o Consent Mode v2 e envia update.
 * @param {{choices: Record<string, boolean>}} snapshot
 */
export function applyConsent(snapshot) {
  if (!snapshot?.choices) return;
  _ensureDataLayer();

  const c = snapshot.choices;
  const analiseUso = !!c.analise_uso;
  const marketing = !!c.marketing;

  window.gtag('consent', 'update', {
    analytics_storage: analiseUso ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  });

  // Evento interno para ver no Realtime se o consent chegou.
  trackEvent('consent_update', {
    analise_uso: analiseUso ? 1 : 0,
    marketing: marketing ? 1 : 0,
    termo_version: snapshot.version || 'unknown',
  });
}

/**
 * Dispara um evento customizado.
 * Parâmetros: nunca PII. Use IDs internos, flags e contadores.
 * @param {string} name
 * @param {Record<string, string|number|boolean>} [params]
 */
export function trackEvent(name, params = {}) {
  try {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', name, params);
  } catch (e) {
    // silencioso
  }
}

// ── Loader do script externo (idempotente) ──────────────────────────────────

function _loadScript() {
  if (_loaded) return;
  _loaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = SCRIPT_SRC;
  s.referrerPolicy = 'no-referrer-when-downgrade';
  document.head.appendChild(s);
}

// ── Helpers semânticos (açúcar sobre trackEvent) ─────────────────────────────

/**
 * Registra login bem-sucedido. Envia só flag is_admin, nunca email/nome.
 * @param {{is_admin?: boolean, method?: string}} meta
 */
export function trackLogin(meta = {}) {
  trackEvent('login', {
    method: meta.method || 'google',
    is_admin: meta.is_admin ? 1 : 0,
  });
}

export function trackLoginFailed(reason = 'unknown') {
  trackEvent('login_failed', { reason: String(reason).slice(0, 50) });
}

export function trackLgpdAcceptAll() {
  trackEvent('lgpd_accept_all', {});
}

export function trackLgpdRevoke(finalidade, granted) {
  trackEvent('lgpd_revoke', {
    finalidade: String(finalidade).slice(0, 40),
    granted: granted ? 1 : 0,
  });
}

/**
 * page_view manual para SPA com hash routing.
 * GA4 automático nem sempre captura hashchange em todos os browsers
 * — emitir explicitamente garante cobertura.
 *
 * Não enviamos nome/email; apenas path de rota. Páginas do tipo
 * `/admin/aluno/:id` devem normalizar o ID antes de chamar aqui.
 *
 * @param {string} path  ex: '/login', '/aluno', '/admin/frequencia'
 * @param {string} [title] título humano opcional
 */
export function trackPageView(path, title) {
  const safePath = String(path || '/').slice(0, 100);
  trackEvent('page_view', {
    page_path: safePath,
    page_location: location.origin + '/#' + safePath,
    page_title: title ? String(title).slice(0, 80) : undefined,
  });
}
